import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import * as nifti from 'nifti-reader-js';
import NiftiFileReader from "@pages/Operations/DataLabellingPage/NiftiFileReader";
import { useParams } from "react-router-dom";
import { getMarkedSlices, saveMarkedSlices } from "@services/dashboardService";
import { showErrorSnackbar, showSuccessSnacbar } from "@components/shared-layouts/Snackbar/helper";
import { Button, Checkbox, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import service from "@services/index";

const Navbar = styled.div`
  height: 75px;
  background: #f04a23;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 
              0px 3px 4px 0px rgba(0,0,0,0.14), 
              0px 1px 8px 0px rgba(0,0,0,0.12);
  justify-content: space-between;
`;

const NavbarTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 900;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  height: calc(100vh - 75px);
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const Sidebar = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f04a23;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
  flex: 1;
`;

const ImageListContainer = styled.div`
  padding: 16px;
  height: calc(100vh - 145px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const SliceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliceListItem = styled.li<{ isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#ffffff'};
  border: 1px solid ${props => props.isSelected ? '#f04a23' : '#e0e0e0'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#f5f5f5'};
    border-color: #f04a23;
  }
`;

const SliceName = styled.span<{ isSelected: boolean }>`
  font-size: 13px;
  color: ${props => props.isSelected ? 'white' : '#333'};
  font-weight: ${props => props.isSelected ? 'bold' : 'normal'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const NiftiViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  color: #f04a23;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 20px;
  border: 1px solid #ed8623;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${props => `${props.progress}%`};
  height: 100%;
  background: linear-gradient(to right, #f04a23, #ed8623);
  transition: width 0.3s ease-in-out;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
  color: #666;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled.h2`
  color: #f04a23;
  margin-bottom: 10px;
  font-size: 24px;
`;

const WelcomeText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
`;

// Utility function to format bytes to human-readable format
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

function SliceMarkingDetailPage() {
  const { projectId, fileName } = useParams<{ projectId: string; fileName: string; project_type: string }>();
  // const navigate = useNavigate();
  
  const [niftiFile, setNiftiFile] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadLoaded, setDownloadLoaded] = useState<number>(0);
  const [downloadTotal, setDownloadTotal] = useState<number>(0);
  const [markedSlices, setMarkedSlices] = useState<Set<number>>(new Set());
  const [selectedSlice, setSelectedSlice] = useState<number | null>(null);
  const [totalSlices, setTotalSlices] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [loadingMarkedSlices, setLoadingMarkedSlices] = useState(false);
  const niftiCache = useRef<Map<string, ArrayBuffer>>(new Map());
  
  const decodedFileName = fileName ? decodeURIComponent(fileName) : '';

  // Load marked slices from API
  const fetchMarkedSlices = useCallback(async () => {
    if (!projectId || !decodedFileName) return;

    try {
      setLoadingMarkedSlices(true);
      const data = await getMarkedSlices(projectId, decodedFileName);
      
      if (data?.marked_slices && Array.isArray(data.marked_slices)) {
        const slices = new Set<number>(data.marked_slices.map((s: string) => parseInt(s, 10)));
        setMarkedSlices(slices);
      }
    } catch (err: any) {
      console.error('Error fetching marked slices:', err);
      // Don't show error snackbar here, just log it
    } finally {
      setLoadingMarkedSlices(false);
    }
  }, [projectId, decodedFileName]);

  // Load NIFTI file from API
  const fetchNiftiFile = useCallback(async (imageId: string) => {
    if (!projectId) return;

    try {
      setNiftiFile(null);
      setLoading(true);
      setError(null);
      setDownloadProgress(0);
      setDownloadLoaded(0);
      setDownloadTotal(0);

      // Check cache first
      if (niftiCache.current.has(imageId)) {
        console.log('Loading from cache:', imageId);
        const cachedFile = niftiCache.current.get(imageId)!;
        setNiftiFile(cachedFile);
        setDownloadProgress(100);
        setLoading(false);
        
        // Extract total slices from cached file
        try {
          const niftiHeader = nifti.readHeader(cachedFile);
          if (niftiHeader && niftiHeader.dims) {
            setTotalSlices(niftiHeader.dims[3] || 0);
            if (niftiHeader.dims[3] && niftiHeader.dims[3] > 0) {
              setSelectedSlice(0); // Select first slice by default
            }
          }
        } catch (e) {
          console.error('Error reading NIFTI header:', e);
        }
        return;
      }

      const response = await service.get(
        `/api/genie-3d/data/${projectId}/image/${imageId}`,
        null,
        null,
        'https://genie-be-3d-dev.avawatz.com',
        { 
          responseType: 'arraybuffer',
          onDownloadProgress: (progressEvent: any) => {
            if (progressEvent.total && progressEvent.total > 0) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              // Only update if valid and within range
              if (!isNaN(percentCompleted) && isFinite(percentCompleted) && percentCompleted >= 0 && percentCompleted <= 100) {
                setDownloadProgress(percentCompleted);
                setDownloadLoaded(progressEvent.loaded);
                setDownloadTotal(progressEvent.total);
              }
            }
          }
        }
      );

      if (!response.data) {
        throw new Error('No data received from the server');
      }

      console.log('Array Buffer Size:', response.data.byteLength);
      
      // Store in cache
      niftiCache.current.set(imageId, response.data);
      
      setDownloadProgress(100);
      
      // Extract total slices from NIFTI header
      try {
        const niftiHeader = nifti.readHeader(response.data);
        if (niftiHeader && niftiHeader.dims) {
          setTotalSlices(niftiHeader.dims[3] || 0);
          if (niftiHeader.dims[3] && niftiHeader.dims[3] > 0) {
            setSelectedSlice(0); // Select first slice by default
          }
        }
      } catch (e) {
        console.error('Error reading NIFTI header:', e);
      }
      
      setNiftiFile(response.data);

    } catch (err) {
      console.error('Error fetching NIFTI file:', err);
      setError(err instanceof Error ? err.message : 'Failed to load NIFTI file.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Load file and marked slices on mount
  useEffect(() => {
    if (decodedFileName) {
      fetchNiftiFile(decodedFileName);
      fetchMarkedSlices();
    }
  }, [decodedFileName, fetchNiftiFile, fetchMarkedSlices]);

  const handleSliceToggle = (sliceIndex: number) => {
    setMarkedSlices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sliceIndex)) {
        newSet.delete(sliceIndex);
      } else {
        newSet.add(sliceIndex);
      }
      return newSet;
    });
  };

  const handleSliceSelect = (sliceIndex: number) => {
    setSelectedSlice(sliceIndex);
  };

  const handleSave = async () => {
    if (!projectId || !decodedFileName) {
      showErrorSnackbar('Project ID or file name is missing');
      return;
    }

    try {
      setSaving(true);
      const markedSlicesArray = Array.from(markedSlices).sort((a, b) => a - b);
      await saveMarkedSlices(projectId, decodedFileName, markedSlicesArray);
      showSuccessSnacbar('Marked slices saved successfully');
    } catch (err: any) {
      console.error('Error saving marked slices:', err);
      const errorMessage = err?.error?.message || 'Failed to save marked slices';
      showErrorSnackbar(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // const handleBack = () => {
  //   if (projectId && project_type) {
  //     navigate(`/${project_type}/operations/${projectId}/slice-marking`);
  //   }
  // };

  return (
    <PageContainer>
      <Navbar>
        <NavbarTitle>Slice Marking - {decodedFileName}</NavbarTitle>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: 'white',
            color: '#f04a23',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Navbar>
      <ContentWrapper>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Slice List</SidebarTitle>
          </SidebarHeader>
          <ImageListContainer>
            {loadingMarkedSlices ? (
              <ProgressContainer>
                <CircularProgress size={24} />
                <ProgressText>Loading marked slices...</ProgressText>
              </ProgressContainer>
            ) : totalSlices > 0 ? (
              <SliceList>
                {Array.from({ length: totalSlices }, (_, index) => (
                  <SliceListItem
                    key={index}
                    isSelected={selectedSlice === index}
                    onClick={() => handleSliceSelect(index)}
                  >
                    <Checkbox
                      checked={markedSlices.has(index)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSliceToggle(index);
                      }}
                      sx={{
                        color: selectedSlice === index ? 'white' : '#f04a23',
                        '&.Mui-checked': {
                          color: selectedSlice === index ? 'white' : '#f04a23',
                        },
                      }}
                    />
                    <SliceName isSelected={selectedSlice === index}>
                      Slice {index}
                    </SliceName>
                  </SliceListItem>
                ))}
              </SliceList>
            ) : (
              <WelcomeContainer>
                <WelcomeText>No slices available</WelcomeText>
              </WelcomeContainer>
            )}
          </ImageListContainer>
        </Sidebar>
        <MainContent>
          {loading && (
            <ProgressContainer>
              <ProgressWrapper>
                <ProgressText>
                  Loading NIFTI file... {downloadProgress}%
                  {downloadTotal > 0 && (
                    <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', color: '#666' }}>
                      {formatBytes(downloadLoaded)} / {formatBytes(downloadTotal)}
                    </span>
                  )}
                </ProgressText>
                <ProgressBarContainer>
                  <ProgressBar progress={downloadProgress} />
                </ProgressBarContainer>
              </ProgressWrapper>
            </ProgressContainer>
          )}
          {error && (
            <div style={{ 
              color: 'red',
              padding: '20px',
              textAlign: 'center' 
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
          {!loading && !niftiFile && !error && (
            <WelcomeContainer>
              <WelcomeIcon />
              <WelcomeTitle>Welcome to Slice Marking</WelcomeTitle>
              <WelcomeText>
                Select a slice from the list on the left to start viewing and marking slices.
              </WelcomeText>
            </WelcomeContainer>
          )}
          {!loading && niftiFile && (
            <NiftiViewerContainer>
              <NiftiFileReader 
                initialNiftiData={niftiFile} 
                projectId={projectId || ''} 
                imageId={decodedFileName} 
                labelsFromContext={[]} 
                preLoadedAnnotations={null}
                externalSliceIndex={selectedSlice !== null ? selectedSlice : undefined}
              />
            </NiftiViewerContainer>
          )}
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
}

export default SliceMarkingDetailPage;

